import { Link } from 'react-router-dom';
import { useMemo } from 'react';
import { Artwork } from '../components/Artwork';
import { db } from '../db/indexedDb';
import { usePlaylists, useTracks } from '../hooks/useIndexedDb';
import { usePlayer } from '../hooks/usePlayer';
import { useToast } from '../hooks/useToast';
import { formatDuration } from '../lib/audio';
import type { Playlist, Track } from '../types';

const EMPTY_TRACKS: Track[] = [];
const EMPTY_PLAYLISTS: Playlist[] = [];

function StatCard({
  label,
  value,
  hint
}: {
  label: string;
  value: string;
  hint: string;
}) {
  return (
    <article className="panel rounded-[1.4rem] p-4 sm:rounded-[2rem] sm:p-5">
      <p className="text-xs uppercase tracking-[0.28em] text-accent">{label}</p>
      <p className="mt-2 text-3xl font-bold sm:mt-3 sm:font-display sm:text-4xl">{value}</p>
      <p className="mt-2 text-sm text-foreground/65">{hint}</p>
    </article>
  );
}

export function HomePage() {
  const trackQuery = useTracks();
  const playlistQuery = usePlaylists();
  const tracks = trackQuery ?? EMPTY_TRACKS;
  const playlists = playlistQuery ?? EMPTY_PLAYLISTS;
  const { currentTrack, playTrack, enqueue } = usePlayer();
  const { pushToast } = useToast();

  const totalDuration = useMemo(
    () => tracks.reduce((sum, track) => sum + track.duration, 0),
    [tracks]
  );
  const recentTracks = useMemo(
    () => [...tracks].sort((a, b) => b.createdAt.localeCompare(a.createdAt)).slice(0, 5),
    [tracks]
  );
  const recentPlaylists = useMemo(() => playlists.slice(0, 3), [playlists]);

  return (
    <section className="min-w-0 space-y-4 sm:space-y-6">
      <div className="grid min-w-0 gap-4 sm:gap-6 xl:grid-cols-[1.35fr,0.95fr]">
        <section className="panel min-w-0 overflow-hidden rounded-[1.75rem] p-4 sm:rounded-[2.25rem] sm:p-8">
          <p className="text-xs uppercase tracking-[0.34em] text-accent">Home</p>
          <h2 className="mt-3 max-w-full break-words font-display text-[1.85rem] font-bold leading-[0.98] sm:mt-4 sm:max-w-2xl sm:text-5xl">
            Your offline library, organized like a real music app.
          </h2>
          <p className="mt-3 max-w-full text-sm leading-6 text-foreground/72 sm:mt-4 sm:max-w-2xl sm:text-base">
            Jump back into listening, upload new tracks, or shape playlists for flights, commutes,
            and anywhere your connection drops.
          </p>

          <div className="mt-5 flex min-w-0 flex-wrap gap-2 sm:mt-8 sm:gap-3">
            <Link
              to="/upload"
              className="whitespace-nowrap rounded-full bg-accent px-4 py-2.5 text-sm font-semibold text-black sm:px-5 sm:py-3"
            >
              Import Songs
            </Link>
            <Link to="/songs" className="pill whitespace-nowrap px-4 py-2.5 text-sm sm:px-5 sm:py-3">
              Browse Library
            </Link>
            <Link
              to="/playlists"
              className="pill whitespace-nowrap px-4 py-2.5 text-sm sm:px-5 sm:py-3"
            >
              Manage Playlists
            </Link>
          </div>

          <div className="mt-5 grid min-w-0 gap-3 sm:mt-8 sm:gap-4 md:grid-cols-3">
            <StatCard
              label="Tracks"
              value={String(tracks.length)}
              hint="Saved locally in IndexedDB"
            />
            <StatCard
              label="Playlists"
              value={String(playlists.length)}
              hint="Mixes for every mood"
            />
            <StatCard
              label="Runtime"
              value={formatDuration(totalDuration)}
              hint="Total imported listening time"
            />
          </div>
        </section>

        <aside className="panel min-w-0 rounded-[1.75rem] p-4 sm:rounded-[2.25rem] sm:p-6">
          <p className="text-xs uppercase tracking-[0.34em] text-accent">Continue Listening</p>
          <div className="mt-4 flex min-w-0 flex-col gap-3 rounded-[1.4rem] border border-white/10 bg-white/5 p-3 sm:mt-5 sm:flex-row sm:items-center sm:gap-4 sm:rounded-[1.75rem] sm:p-4">
            <Artwork track={currentTrack} size="lg" className="h-20 w-20 rounded-[1.2rem] sm:h-32 sm:w-32 sm:rounded-3xl" />
            <div className="min-w-0 flex-1">
              <p className="text-lg font-semibold leading-tight sm:truncate sm:font-display sm:text-2xl">
                {currentTrack?.title ?? 'Nothing playing yet'}
              </p>
              <p className="mt-1 text-sm text-foreground/65 sm:truncate">
                {currentTrack
                  ? `${currentTrack.artist} • ${currentTrack.album}`
                  : 'Pick a track from your library to start a session.'}
              </p>
              <div className="mt-4 flex min-w-0 flex-col gap-2 sm:mt-5 sm:flex-row sm:flex-wrap sm:gap-3">
                <Link
                  to="/now-playing"
                  className="rounded-full bg-accent px-4 py-2 text-center text-sm font-semibold text-black"
                >
                  Open Player
                </Link>
                {recentTracks[0] ? (
                  <button
                    type="button"
                    onClick={() => void playTrack(recentTracks[0].id, recentTracks.map((track) => track.id))}
                    className="pill justify-center"
                  >
                    Play Recent
                  </button>
                ) : null}
              </div>
            </div>
          </div>

          <div className="mt-4 rounded-[1.4rem] border border-white/10 bg-white/5 p-3 sm:mt-6 sm:rounded-[1.75rem] sm:p-4">
            <p className="text-xs uppercase tracking-[0.24em] text-foreground/55">Offline Ready</p>
            <p className="mt-2 text-sm text-foreground/70">
              Once tracks are imported on this device, browsing and playback continue offline.
            </p>
            <Link to="/upload" className="mt-4 inline-flex text-sm font-medium text-accent">
              Add more songs
            </Link>
          </div>
        </aside>
      </div>

      <div className="grid min-w-0 gap-4 sm:gap-6 xl:grid-cols-[1.15fr,0.85fr]">
        <section className="panel min-w-0 rounded-[1.75rem] p-4 sm:rounded-[2rem] sm:p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-accent">Recently Added</p>
              <h3 className="mt-2 font-display text-2xl font-semibold">Fresh imports</h3>
            </div>
            <Link to="/songs" className="pill">
              View all songs
            </Link>
          </div>

          <ul className="mt-4 space-y-3 sm:mt-6">
            {recentTracks.length ? (
              recentTracks.map((track) => (
                <li
                  key={track.id}
                  className="flex min-w-0 items-center gap-3 rounded-2xl border border-white/10 bg-white/5 p-4"
                >
                  <Artwork track={track} size="md" />
                  <button
                    type="button"
                    onClick={() => void playTrack(track.id, recentTracks.map((item) => item.id))}
                    className="min-w-0 flex-1 text-left"
                  >
                    <p className="truncate font-semibold hover:text-accent">{track.title}</p>
                    <p className="mt-1 truncate text-sm text-foreground/65">
                      {track.artist} • {track.album}
                    </p>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      enqueue([track.id]);
                      pushToast({ title: 'Queued', description: `${track.title} added to queue.` });
                    }}
                    className="pill shrink-0"
                  >
                    Queue
                  </button>
                </li>
              ))
            ) : (
              <li className="rounded-2xl border border-white/10 bg-white/5 p-5 text-sm text-foreground/70">
                No tracks yet. Head to Upload and drop in your first songs.
              </li>
            )}
          </ul>
        </section>

        <section className="panel min-w-0 rounded-[1.75rem] p-4 sm:rounded-[2rem] sm:p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-accent">Playlists</p>
              <h3 className="mt-2 font-display text-2xl font-semibold">Quick access</h3>
            </div>
            <Link to="/playlists" className="pill">
              Open playlists
            </Link>
          </div>

          <ul className="mt-4 space-y-3 sm:mt-6">
            {recentPlaylists.length ? (
              recentPlaylists.map((playlist) => (
                <li
                  key={playlist.id}
                  className="rounded-2xl border border-white/10 bg-white/5 p-4"
                >
                  <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                    <div className="min-w-0">
                      <p className="truncate font-semibold">{playlist.name}</p>
                      <p className="mt-1 text-sm text-foreground/65">
                        {playlist.trackIds.length} tracks
                      </p>
                    </div>
                    <Link
                      to={`/playlists/${playlist.id}`}
                      className="rounded-full bg-accent px-4 py-2 text-sm font-semibold text-black"
                    >
                      Open
                    </Link>
                  </div>
                </li>
              ))
            ) : (
              <li className="rounded-2xl border border-white/10 bg-white/5 p-5 text-sm text-foreground/70">
                No playlists yet. Build your first mix from the library page.
              </li>
            )}
          </ul>

          <div className="mt-4 rounded-[1.4rem] border border-white/10 bg-white/5 p-3 sm:mt-6 sm:rounded-[1.75rem] sm:p-4">
            <p className="text-xs uppercase tracking-[0.24em] text-foreground/55">Tip</p>
            <p className="mt-2 text-sm text-foreground/70">
              Select multiple songs in the library to add them to a playlist or queue in one move.
            </p>
          </div>
        </section>
      </div>
    </section>
  );
}
